import { useMemo } from 'react';
import dayjs from 'dayjs';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { isValidDate } from '@frontend/schedule-helpers';
import { DATETIME_FORMATS, MIN_APPT_TYPE_DURATION_IN_MINUTES } from '../../../../../../constants';
import { useSchedulingLocationInfo } from '../../../../../../hooks/use-scheduling-location-info';
import { getDateByTimezone, isSameOrAfterDate } from '../../../../../../utils/date-helpers';
import { useManageScheduleRequestModalForm } from '../hooks/useManageScheduleRequestModalForm';
import { ReducedAppointmentType } from '../hooks/useReviewRequest';

type useGetOperatoryAvailabilityParams = {
  appointmentTypes?: ReducedAppointmentType[];
  locationId?: string;
  isIntegratedOffice?: boolean;
  customDateField: ReturnType<typeof useManageScheduleRequestModalForm>['customDateField'];
  customStartTimeField: ReturnType<typeof useManageScheduleRequestModalForm>['customStartTimeField'];
  customEndTimeField: ReturnType<typeof useManageScheduleRequestModalForm>['customEndTimeField'];
  values: ReturnType<typeof useManageScheduleRequestModalForm>['scheduleRequestApprovalForm']['values'];
};

export const useGetAvailableOperatoriesForSlot = ({
  appointmentTypes,
  locationId = '',
  isIntegratedOffice,
  customDateField,
  customStartTimeField,
  customEndTimeField,
  values,
}: useGetOperatoryAvailabilityParams) => {
  const { getLocationTimeZone } = useSchedulingLocationInfo();

  const { payload, isPayloadValid } = useMemo(() => {
    const selectedProviderIds = values.provider ? [values.provider] : [];
    const selectedApptTypeDuration =
      appointmentTypes?.find((apptType) => apptType.bookableAppointmentTypeId === values.appointmentType)
        ?.durationMinutes || MIN_APPT_TYPE_DURATION_IN_MINUTES;

    let startDateTime, endDateTime, durationMinutes;
    const locationTimezone = getLocationTimeZone(locationId);

    if (values.dateAndTime === 'custom') {
      const customDate = customDateField.value; // MM/DD/YYYY
      const customStartTime = customStartTimeField.value; // HH:mm:ss
      const customEndTime = customEndTimeField.value; // HH:mm:ss

      const startDateTimeInOfficeTimezone = getDateByTimezone(
        `${customDate} ${customStartTime}`,
        DATETIME_FORMATS.US_DATE_TIME,
        locationTimezone
      );

      const customDuration = dayjs(
        getDateByTimezone(`${customDate} ${customEndTime}`, DATETIME_FORMATS.US_DATE_TIME, locationTimezone)
      ).diff(startDateTimeInOfficeTimezone, 'minute');

      startDateTime = startDateTimeInOfficeTimezone.startOf('day').format(DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET);
      endDateTime = startDateTimeInOfficeTimezone.endOf('day').format(DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET);
      durationMinutes = customDuration || selectedApptTypeDuration;
    } else {
      const utcDateTime = values.dateAndTime; // Format: YYYY-MM-DDTHH:mm:ssZ
      const dateInOfficeTimezone = dayjs.utc(utcDateTime).tz(locationTimezone);
      startDateTime = dateInOfficeTimezone.startOf('day').format(DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET);
      endDateTime = dateInOfficeTimezone.endOf('day').format(DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET);
      durationMinutes = selectedApptTypeDuration;
    }

    const isFutureDateSelected =
      isValidDate(dayjs(startDateTime)) && isSameOrAfterDate(dayjs(startDateTime), dayjs().startOf('day'));

    return {
      payload: {
        locationId,
        providerIds: selectedProviderIds,
        startDateTime: startDateTime || '',
        endDateTime: endDateTime || '',
        durationMinutes: durationMinutes,
        clientTimezone: dayjs.tz.guess(),
      },
      isPayloadValid: !!(
        locationId &&
        selectedProviderIds.length > 0 &&
        startDateTime &&
        endDateTime &&
        durationMinutes &&
        isFutureDateSelected
      ),
    };
  }, [
    appointmentTypes,
    locationId,
    values.appointmentType,
    values.provider,
    values.dateAndTime,
    customDateField,
    customStartTimeField,
    customEndTimeField,
    getLocationTimeZone,
  ]);

  const availabilityQuery = SchedulerV3.Queries.useListBookableAppointmentSlotsQuery({
    request: {
      startDateTime: payload.startDateTime,
      endDateTime: payload.endDateTime,
      locationId: payload.locationId,
      providerIds: payload.providerIds,
      slotDuration: payload.durationMinutes,
    },
    options: {
      enabled: isIntegratedOffice && isPayloadValid,
    },
  });

  const providerSlots = availabilityQuery.data?.providerSlots ?? [];

  // Extract unique operatory IDs from the returned provider slots
  const availableOperatoryIds = new Set(
    providerSlots.flatMap((providerSlot) => providerSlot.slots ?? []).map((slot) => slot.operatoryId)
  );

  return { availableOperatoryIds, isLoadingAvailableOperatoryIds: availabilityQuery.isLoading };
};
